import React from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiClock,FiDollarSign,FiZap,FiTrendingUp,FiUsers,FiEdit3,FiMail,FiSmartphone,FiSearch,FiTarget,FiVideo,FiCheck}=FiIcons;

function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Stop Juggling Tools. <br />
              <span className="text-blue-200">Start Generating Leads.</span>
            </h1>
            <div className="flex items-center justify-center space-x-2 text-blue-100 text-lg">
              <span>Powered by</span>
              <span className="font-semibold text-white">Handvantage</span>
              <span>Enterprise Solutions</span>
            </div>
          </div>
          <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
            AllGrowth by Handvantage helps local businesses grow with AI-powered content marketing that builds trust,drives traffic,and converts visitors into customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book-consultation"
              className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Get Your Strategy Call
            </Link>
            <Link
              to="/services"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-900 transition-colors"
            >
              See Our Work
            </Link>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Local Marketing Problem
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Local business owners are experts at their craft,not marketing specialists.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="text-blue-600 mb-4">
                <SafeIcon icon={FiClock} className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">No Time for Marketing</h3>
              <p className="text-gray-600">
                You're busy running your business. Creating consistent,quality content takes time you don't have.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="text-blue-600 mb-4">
                <SafeIcon icon={FiDollarSign} className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Enterprise Costs</h3>
              <p className="text-gray-600">
                Solutions like HubSpot are powerful but priced for corporations,not growing local businesses.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="text-blue-600 mb-4">
                <SafeIcon icon={FiZap} className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Too Many Tools</h3>
              <p className="text-gray-600">
                Multiple disconnected marketing tools create complexity and waste time with no clear ROI.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The AllGrowth Marketing Engine
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              We've bundled the three core drivers of growth into one streamlined,affordable solution.
            </p>
            <div className="text-sm text-gray-500">
              Built on <span className="font-semibold text-gray-700">Handvantage's</span> enterprise-grade infrastructure
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <SafeIcon icon={FiTrendingUp} className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Growth</h3>
                  <p className="text-gray-600">
                    Fill your pipeline with high-quality leads through targeted campaigns and content marketing.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <SafeIcon icon={FiZap} className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Automation</h3>
                  <p className="text-gray-600">
                    Put your marketing on autopilot with powerful CRM and automated workflows.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <SafeIcon icon={FiUsers} className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Visibility</h3>
                  <p className="text-gray-600">
                    Be the go-to choice in your niche through expert SEO and local optimization.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What You Get</h3>
              <ul className="space-y-4">
                {[
                  'AI-powered blog content that ranks and converts',
                  'Email marketing campaigns that nurture leads',
                  'Social media posts that build local trust',
                  'SEO-optimized pages that dominate local search',
                  'Lead magnets that capture email addresses',
                  'Monthly strategy calls and performance reports'
                ].map((item,index)=> (
                  <li key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              AllGrowth Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to grow your local business,delivered as done-for-you solutions.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {icon: FiEdit3,title: 'AI Blog Content',description: 'Build long-term trust and search visibility with helpful,localized content.'},
              {icon: FiMail,title: 'Email Marketing',description: 'Nurture leads and turn attention into action with strategic campaigns.'},
              {icon: FiSmartphone,title: 'Social Media Posts',description: 'Build daily familiarity with local audiences using engaging content.'},
              {icon: FiSearch,title: 'Local SEO Pages',description: 'Dominate local search results with optimized,conversion-focused pages.'},
              {icon: FiTarget,title: 'Lead Magnets',description: 'Create downloadable guides that position you as the go-to expert.'},
              {icon: FiVideo,title: 'YouTube Scripts',description: 'Powerful video scripts that establish authority and drive bookings.'}
            ].map((service,index)=> (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="text-blue-600 mb-4">
                  <SafeIcon icon={service.icon} className="h-10 w-10" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Handvantage Trust Section */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Backed by Handvantage's Proven Expertise
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              AllGrowth is powered by Handvantage Corporation's enterprise-grade technology and years of experience helping businesses achieve sustainable growth through digital transformation.
            </p>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                <p className="text-gray-600">Businesses Transformed</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">$50M+</div>
                <p className="text-gray-600">Revenue Generated</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">98%</div>
                <p className="text-gray-600">Client Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Marketing?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
            Book a strategy call and discover how AllGrowth can help you attract more leads,build trust,and grow your business.
          </p>
          <Link
            to="/book-consultation"
            className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            Book Your Strategy Call
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;